/**
 * ResultadoCaptureSheet — Side panel for capturing exam results.
 *
 * Shows exam data + capture form.
 * Renders field based on type: numerico, texto, opciones.
 * Shows reference values next to the field.
 * Optional observations field.
 * Save button with loading state.
 * On success: close sheet, invalidate queries, show toast.
 *
 * Uses react-hook-form + zod for the form.
 * Uses useCapturarResultado mutation.
 *
 * Requirements: 3.1–3.9, 4.7, 6.4
 */

import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { FlaskConical, Save, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";

import {
  useCapturarResultado,
  useResultado,
  type ColaTrabajoItem,
} from "@/hooks/use-procesamiento";
import { useApiError } from "@/hooks/use-api-error";

import { ResultadoNumericoField } from "./resultado-numerico-field";
import { ResultadoTextoField } from "./resultado-texto-field";
import { ResultadoOpcionesField } from "./resultado-opciones-field";
import { ValoresReferenciaDisplay } from "./valores-referencia-display";

// ─── Schema ────────────────────────────────────────────────────────────────

const captureSchema = z.object({
  valor_numerico: z.string().optional(),
  valor_texto: z.string().max(2000).optional(),
  valor_opcion: z.string().optional(),
  observaciones: z.string().max(2000).optional(),
});

type CaptureFormData = z.infer<typeof captureSchema>;

// ─── Props ─────────────────────────────────────────────────────────────────

interface ResultadoCaptureSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: ColaTrabajoItem | null;
  onSuccess: () => void;
}

export function ResultadoCaptureSheet({
  open,
  onOpenChange,
  item,
  onSuccess,
}: ResultadoCaptureSheetProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();

  // Fetch existing resultado if any
  const { data: resultado } = useResultado(item?.id ?? null);

  // Mutation
  const capturarMutation = useCapturarResultado({
    onSuccess: () => {
      toast.success(t("procesamiento.captura.guardado_exitoso"));
      onSuccess();
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    },
  });

  // Form
  const form = useForm<CaptureFormData>({
    resolver: zodResolver(captureSchema),
    defaultValues: {
      valor_numerico: "",
      valor_texto: "",
      valor_opcion: "",
      observaciones: "",
    },
  });

  // Reset form when item changes
  useEffect(() => {
    if (item) {
      form.reset({
        valor_numerico: resultado?.valor_numerico ?? "",
        valor_texto: resultado?.valor_texto ?? "",
        valor_opcion: resultado?.valor_opcion ?? "",
        observaciones: resultado?.observaciones ?? "",
      });
    }
  }, [item, resultado, form]);

  // Submit handler
  const onSubmit = (data: CaptureFormData) => {
    if (!item) return;

    capturarMutation.mutate({
      orden_examen_id: item.id,
      valor_numerico: item.tipo_resultado === "NUMERICO" ? data.valor_numerico || null : undefined,
      valor_texto: item.tipo_resultado === "TEXTO" ? data.valor_texto : undefined,
      valor_opcion: item.tipo_resultado === "OPCIONES" ? data.valor_opcion : undefined,
      observaciones: data.observaciones || undefined,
    });
  };

  if (!item) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <FlaskConical className="h-5 w-5" />
            {t("procesamiento.captura.titulo")}
          </SheetTitle>
          <SheetDescription>{t("procesamiento.captura.descripcion")}</SheetDescription>
        </SheetHeader>

        {/* Exam Info */}
        <div className="px-4 space-y-3">
          <div className="rounded-lg border p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{item.examen_nombre}</span>
              <Badge variant="outline">{item.tipo_resultado}</Badge>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
              <div>
                <span className="font-medium">{t("procesamiento.captura.paciente")}:</span>{" "}
                {item.paciente_nombre}
              </div>
              <div>
                <span className="font-medium">{t("procesamiento.captura.orden")}:</span>{" "}
                {item.numero_orden}
              </div>
              <div>
                <span className="font-medium">{t("procesamiento.captura.area")}:</span>{" "}
                {item.examen_area_display}
              </div>
              <div>
                <span className="font-medium">{t("procesamiento.captura.codigo")}:</span>{" "}
                {item.examen_codigo}
              </div>
            </div>
          </div>

          <Separator />

          {/* Reference Values */}
          {resultado?.valores_referencia && resultado.valores_referencia.length > 0 && (
            <>
              <ValoresReferenciaDisplay valores={resultado.valores_referencia} />
              <Separator />
            </>
          )}

          {/* Capture Form */}
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Dynamic field based on tipo_resultado */}
            {item.tipo_resultado === "NUMERICO" && (
              <ResultadoNumericoField
                value={form.watch("valor_numerico") ?? ""}
                onChange={(val) => form.setValue("valor_numerico", val)}
                error={form.formState.errors.valor_numerico?.message}
                unidad={resultado?.unidad}
              />
            )}

            {item.tipo_resultado === "TEXTO" && (
              <ResultadoTextoField
                value={form.watch("valor_texto") ?? ""}
                onChange={(val) => form.setValue("valor_texto", val)}
                error={form.formState.errors.valor_texto?.message}
              />
            )}

            {item.tipo_resultado === "OPCIONES" && (
              <ResultadoOpcionesField
                value={form.watch("valor_opcion") ?? ""}
                onChange={(val) => form.setValue("valor_opcion", val)}
                opciones={resultado?.opciones_resultado ?? []}
                error={form.formState.errors.valor_opcion?.message}
              />
            )}

            {/* Observations */}
            <div className="space-y-2">
              <Label htmlFor="observaciones">{t("procesamiento.captura.observaciones")}</Label>
              <Textarea
                id="observaciones"
                placeholder={t("procesamiento.captura.observaciones_placeholder")}
                {...form.register("observaciones")}
                className="min-h-[80px]"
                maxLength={2000}
              />
              <p className="text-xs text-muted-foreground text-right">
                {(form.watch("observaciones") ?? "").length}/2000
              </p>
            </div>

            {/* Submit */}
            <SheetFooter className="p-0">
              <Button type="submit" disabled={capturarMutation.isPending} className="w-full">
                {capturarMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {t("procesamiento.captura.guardar")}
              </Button>
            </SheetFooter>
          </form>
        </div>
      </SheetContent>
    </Sheet>
  );
}
